import React, { useMemo } from "react";
import Container from "@cloudscape-design/components/container";
import { TransformedTreeNode } from "../types";
import { useTreeSelectionStore } from "../../../../store/tree-table-store";
import {
  calculateMetricsForSelectedNodes,
  calculateTotalVariance,
  calculateTotalPctOfRoot,
} from "../utils/metricsCalculator";
import "./selectedItems.scss";

export interface SelectedItemsProps {
  selectedNodes: TransformedTreeNode[];
}

const SelectedItems: React.FC<SelectedItemsProps> = ({
  selectedNodes,
}) => {
  const { originalTreeData } = useTreeSelectionStore();

  const { totalVariance, totalPctOfRoot, rootValue } = useMemo(() => {
    const selectedKeys = new Set(selectedNodes.map((node) => node.key));
    const metricsMap = calculateMetricsForSelectedNodes(
      originalTreeData,
      selectedKeys
    );
    const totalVariance = calculateTotalVariance(
      originalTreeData,
      selectedKeys,
      metricsMap
    );
    const rootValue = originalTreeData?.value ?? 0;
    const totalPctOfRoot = calculateTotalPctOfRoot(totalVariance, rootValue);

    return { totalVariance, totalPctOfRoot, rootValue };
  }, [selectedNodes, originalTreeData]);

  // Early return if no nodes selected - placed after all hooks
  if (selectedNodes.length === 0) {
    return null;
  }

  const formatValue = (value: number): string => {
    const rounded = Math.round(value);
    return rounded >= 0 ? `+${rounded}` : `${rounded}`;
  };

  const formatPercentage = (pct: number): string => {
    return `${Math.abs(pct).toFixed(1)}%`;
  };

  return (
    <Container>
      <div className="selected-items-content">
        {/* Total Variance Summary */}
        <div className="variance-summary">
          <div className="variance-title">Total Selected Variance</div>
          <div
            className={`variance-value ${
              totalVariance < 0 ? "negative" : "positive"
            }`}
          >
            {formatValue(totalVariance)} bps
          </div>
          <div className="variance-percentage">
            ({formatPercentage(totalPctOfRoot)} of total)
          </div>
        </div>
      </div>
    </Container>
  );
};

export default SelectedItems;
